import { defineStore } from 'pinia'
import { useApi } from '@/api/useApi'
import { useBaseStore } from '@haskoning/vue-basestore'
import API from '@/api/apiUrls'
import type { QuantityBuildingBlock } from './type/quantityBuildingBlock.type'
import type { DesignLinkImportValidationRow } from '@/components/building-block-form/utils/switchValidation'

// we import from QUANTITIES not from QUANTITY_BUILDING_BLOCK
const importURL = API.CALCULATION.QUANTITIES.IMPORT_EXCEL
const serviceURL = API.CALCULATION.QUANTITY_BUILDING_BLOCKS.INDEX

export type QuantityImportResult = {
  created: number
  updated: number
  total_rows: number
  design_link_validation_summary?: {
    checked_rows: number
    missing_rows: number
    changed_rows: number
    is_import_safe: boolean
  }
  design_link_missing_rows?: DesignLinkImportValidationRow[]
  design_link_changed_rows?: DesignLinkImportValidationRow[]
}

const toDesignLinkImportValidationRows = (
  rows: unknown
): DesignLinkImportValidationRow[] | undefined => {
  if (!Array.isArray(rows)) return undefined
  return rows
    .filter((row) => typeof row === 'object' && row !== null)
    .map((row) => {
      const record = row as Record<string, unknown>
      return {
        row_index: Number(record.row_index ?? 0),
        quantity_row_id: Number(record.quantity_row_id ?? 0),
        row_name: String(record.row_name ?? ''),
        block_id:
          record.block_id == null || record.block_id === '' ? null : Number(record.block_id),
        block_name: String(record.block_name ?? ''),
        unit: String(record.unit ?? ''),
        changed_fields:
          record.changed_fields && typeof record.changed_fields === 'object'
            ? (record.changed_fields as DesignLinkImportValidationRow['changed_fields'])
            : undefined
      }
    })
}

export const useQuantityBuildingBlockStore = defineStore('quantityBuildingBlock', () => {
  const { api } = useApi()
  const baseStore = useBaseStore<QuantityBuildingBlock>({ service: serviceURL, api })

  const updateFile = async (formData: FormData): Promise<QuantityImportResult> => {
    const response = (await api.service(importURL).post(formData)) as Record<string, unknown>

    return {
      created: Number(response.created ?? 0),
      updated: Number(response.updated ?? 0),
      total_rows: Number(response.total_rows ?? 0),
      design_link_validation_summary:
        response.design_link_validation_summary &&
        typeof response.design_link_validation_summary === 'object'
          ? {
              checked_rows: Number(
                (response.design_link_validation_summary as Record<string, unknown>).checked_rows ??
                  0
              ),
              missing_rows: Number(
                (response.design_link_validation_summary as Record<string, unknown>).missing_rows ??
                  0
              ),
              changed_rows: Number(
                (response.design_link_validation_summary as Record<string, unknown>).changed_rows ??
                  0
              ),
              is_import_safe: Boolean(
                (response.design_link_validation_summary as Record<string, unknown>).is_import_safe
              )
            }
          : undefined,
      design_link_missing_rows: toDesignLinkImportValidationRows(response.design_link_missing_rows),
      design_link_changed_rows: toDesignLinkImportValidationRows(response.design_link_changed_rows)
    }
  }

  const renameLabel = async (calculation: number, from: string, to: string): Promise<void> => {
    await api.service(`${serviceURL}rename-label/`).post({
      calculation,
      from_value: from,
      to_value: to
    })
  }

  return {
    ...baseStore,
    updateFile,
    renameLabel
  }
})
